/**
 * MD-11 FE-CHAT — Phase A API wrapper (POST /api/v1/ask streaming).
 *
 * Added 2026-05-11 per DECISIONS 2026-05-11-MD11-CHATGPT-LAYOUT R1.
 *
 * Why fetch + ReadableStream instead of EventSource:
 *   EventSource is GET-only; we need POST to carry `history?[]` (anon Phase A
 *   multi-turn). Parse SSE frames manually using the shared
 *   `parseSseChunk` parser (workers/chat/sse-parser).
 *
 * Caller responsibilities:
 *   - Resolve anon_session_id + lang externally
 *   - Slice last 6 turn from local thread state, trim 500 char per turn
 *   - Pass auth bearer token if authed (do NOT send history when authed —
 *     server-side MD-7 memory wins; the helper also drops history if
 *     `isAuthed === true` to defend against accidental leak)
 */

import { parseSseChunk, type ParseResult } from '../../../src/workers/chat/sse-parser';
import type { GroundedSourceDoc, SseEvent } from '../../../src/workers/chat/sse-types';

export type { GroundedSourceDoc } from '../../../src/workers/chat/sse-types';

export type AskHistoryTurn = {
  role: 'user' | 'assistant';
  content: string;
};

export const HISTORY_MAX_TURNS = 6;
export const HISTORY_MAX_CHARS = 500;

export type AskStreamOptions = {
  query: string;
  session_id?: string;
  lang?: 'vi' | 'en';
  reset?: boolean;
  /** Phase A: client-supplied last-6-turn anon multi-turn context. */
  history?: AskHistoryTurn[];
  /** When true, history is dropped client-side (server uses MD-7 memory). */
  isAuthed?: boolean;
  /** Bearer token if authed. */
  accessToken?: string;
  /** Called for each parsed SSE event. */
  onEvent: (ev: SseEvent) => void;
  /** Called on fetch/network/parse failure. */
  onError: (err: Error) => void;
  /** Called when stream ends naturally (no `done` event still triggers close). */
  onClose?: () => void;
  /** Abort signal — caller can cancel the in-flight stream. */
  signal?: AbortSignal;
  /** Endpoint override (default: '/api/ask' — Phase A wires to M1 handler). */
  endpoint?: string;
  /** Custom fetch for tests. */
  fetchImpl?: typeof fetch;
};

/**
 * Ask Grounded RAG (MVP) - extract doc-level sources from an SSE event.
 *
 * The grounding seam emits `{type:'sources', payload:{docs:[...]}}` once,
 * before the first answer token (see design spec §11). This helper lets the
 * chat consumer collect the docs into a per-message sources state array while
 * keeping token/refusal/done/error handling unchanged. Returns `null` for any
 * non-sources event OR a malformed payload, so an absent/empty grounding event
 * degrades to "no panel" gracefully (backend grounding is flag-gated and may
 * not be live yet).
 */
export function extractGroundedSources(ev: SseEvent): GroundedSourceDoc[] | null {
  if (ev.type !== 'sources') return null;
  const docs = (ev as { payload?: { docs?: unknown } }).payload?.docs;
  if (!Array.isArray(docs) || docs.length === 0) return null;
  // Defensive: only keep rows that carry the minimum identity fields. A
  // malformed row never crashes the panel - it is simply dropped.
  const valid = docs.filter(
    (d): d is GroundedSourceDoc =>
      !!d &&
      typeof d === 'object' &&
      typeof (d as GroundedSourceDoc).doc_id === 'string' &&
      typeof (d as GroundedSourceDoc).doc_num === 'string'
  );
  return valid.length > 0 ? valid : null;
}

/**
 * Phan 2B (BUG-2026-06-15) - extract the cited doc_ids from the post-stream meta.
 *
 * The backend emits `{type:'meta', payload:{cited_doc_ids:[...]}}` AFTER the answer
 * is fully streamed (it knows then which retrieved sources the answer referenced).
 * The chat consumer flips `cited=true` on the matching `latestSources` rows. Returns
 * `null` for any non-cited-meta event (e.g. the `citation_masked` audit meta), so
 * the caller can ignore it. An empty `cited_doc_ids` array returns `[]` (a real
 * "answer cited nothing of the retrieved set" signal), distinct from `null`.
 */
export function extractCitedDocIds(ev: SseEvent): string[] | null {
  if (ev.type !== 'meta') return null;
  const ids = (ev as { payload?: { cited_doc_ids?: unknown } }).payload?.cited_doc_ids;
  if (!Array.isArray(ids)) return null;
  return ids.filter((x): x is string => typeof x === 'string');
}

/**
 * Phan 2B (BUG-2026-06-15) - reduce the retrieval set to the docs the ANSWER cites.
 *
 * Founder decision 2026-06-15 (review): the "Van ban trich dan" panel must map 1-1
 * with the answer's own citations - show ONLY the cited docs, never the retrieved-
 * but-not-cited noise. So this returns ONLY the sources whose doc_id is in citedIds
 * (the backend's citedSourceDocIds: so hieu OR name match against the answer). When
 * the answer cites NOTHING (citedIds empty - e.g. a "no relevant data" fallback),
 * returns [] so the panel hides entirely (the panel renders null on empty). Pure
 * function for unit-test coverage.
 */
export function applyCitedDocIds(
  sources: GroundedSourceDoc[],
  citedIds: string[]
): GroundedSourceDoc[] {
  if (citedIds.length === 0) return [];
  const set = new Set(citedIds);
  return sources.filter((d) => set.has(d.doc_id)).map((d) => ({ ...d, cited: true }));
}

/** Cap + sanitize history per Phase A rules. */
export function clampHistory(
  history: AskHistoryTurn[] | undefined,
  isAuthed: boolean
): AskHistoryTurn[] | undefined {
  if (!history || history.length === 0) return undefined;
  if (isAuthed) return undefined; // authed -> server-side memory only
  const sliced = history.slice(-HISTORY_MAX_TURNS);
  return sliced
    .filter(t => (t.role === 'user' || t.role === 'assistant') && typeof t.content === 'string')
    .map(t => ({
      role: t.role,
      content: t.content.length > HISTORY_MAX_CHARS ? t.content.slice(0, HISTORY_MAX_CHARS) : t.content,
    }));
}

/**
 * Start an SSE stream from POST /api/v1/ask. Returns a promise that
 * resolves when the stream ends (either via `done` event, network close,
 * or abort).
 */
export async function askStream(opts: AskStreamOptions): Promise<void> {
  // Phase A: point to M1 existing endpoint /api/ask (handled by
  // src/workers/agent/index.ts). The eventual /api/v1/ask route is deferred
  // to Phase B. See specs/DECISIONS.md s2026-05-11-CHAT-FE-USE-M1-ENDPOINT.
  const endpoint = opts.endpoint ?? '/api/ask';
  const fetchImpl = opts.fetchImpl ?? fetch;
  const isAuthed = Boolean(opts.isAuthed);
  const history = clampHistory(opts.history, isAuthed);

  const body: Record<string, unknown> = {
    query: opts.query,
  };
  if (opts.session_id) body.session_id = opts.session_id;
  if (opts.lang) body.lang = opts.lang;
  if (opts.reset) body.reset = opts.reset;
  if (history && history.length > 0) body.history = history;

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    Accept: 'text/event-stream',
  };
  if (opts.accessToken) headers.Authorization = `Bearer ${opts.accessToken}`;
  // Anon principal: the server resolves an anonymous caller ONLY from the
  // X-Anon-Thread-Id header (src/workers/api/ask.ts:785/829), never from the
  // body. Without it an anon request (no accessToken) gets 400 "principal
  // required" (BUG-2026-06-01 /chat). session_id IS the anon thread identity,
  // so forward it as the header when not authed. Mirrors the working /ask
  // route (pages/src/routes/AskChat.tsx:613).
  else if (opts.session_id) headers['X-Anon-Thread-Id'] = opts.session_id;

  let res: Response;
  try {
    res = await fetchImpl(endpoint, {
      method: 'POST',
      headers,
      body: JSON.stringify(body),
      signal: opts.signal,
    });
  } catch (e) {
    opts.onError(e instanceof Error ? e : new Error(String(e)));
    return;
  }

  if (!res.ok || !res.body) {
    opts.onError(new Error(`ask stream HTTP ${res.status}`));
    return;
  }

  const reader = res.body.getReader();
  const decoder = new TextDecoder('utf-8');
  let pending = '';

  try {
    // eslint-disable-next-line no-constant-condition
    while (true) {
      const { value, done } = await reader.read();
      if (done) break;
      pending += decoder.decode(value, { stream: true });

      // parseSseChunk consumes complete frames + returns remainder.
      const parsed: ParseResult = parseSseChunk(pending);
      pending = parsed.remainder;
      for (const ev of parsed.events) opts.onEvent(ev);
    }
  } catch (e) {
    if ((e as { name?: string }).name === 'AbortError') {
      // user cancelled; not an error
    } else {
      opts.onError(e instanceof Error ? e : new Error(String(e)));
    }
  } finally {
    opts.onClose?.();
  }
}
